import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import { tracker } from '../lib/tracker';
import { useScorePageData } from '../hooks/useScorePageData';
import { useScoreData } from '../hooks/useScoreData';
import { getValidatedToken, TokenError } from '../utils/tokenValidation';
import { SectionType, Score } from '../types/score';

// Import des composants
import ScoreHeroSection from '../components/score/ScoreHeroSection';
import PerformanceAnalysisSection from '../components/score/PerformanceAnalysisSection';
import RecommendationsSection from '../components/score/RecommendationsSection';
import TidyCalSection from '../components/score/TidyCalSection';

// Import du CSS externe
import '../styles/animations.css';

const ScorePage: React.FC = () => {
  const { token: pathToken } = useParams<{ token?: string }>();
  const [activeSection, setActiveSection] = useState<SectionType>('overview');
  
  // Validation et récupération sécurisée du token
  const effectiveToken = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return getValidatedToken(urlParams, pathToken);
  }, [pathToken]);

  // Utiliser le hook pour récupérer toutes les données
  const {
    overallScore,
    siteUrl,
    instantPageAnalysis,
    pagespeedResults,
    performanceMetrics,
    loading,
    error,
    refreshData
  } = useScorePageData(effectiveToken || undefined);

  // Utiliser le hook pour calculer les scores dynamiquement
  const score = useScoreData(overallScore, performanceMetrics, siteUrl);

  // Callback mémorisé pour le changement de section
  const handleSectionChange = useCallback((section: string) => {
    setActiveSection(section as SectionType);
  }, []);

  // Tracking de la vue de la page avec gestion d'erreur améliorée
  useEffect(() => {
    const trackView = async () => {
      if (!effectiveToken) {
        console.warn('Token manquant pour le tracking');
        return;
      }

      try {
        await tracker.trackScoreView(effectiveToken);
      } catch (trackingError) {
        console.error('Erreur lors du tracking de la vue du score:', trackingError);
        // Optionnel : Reporter l'erreur à un service de monitoring
      }
    };
    
    trackView();
  }, [effectiveToken]);

  // Composant de chargement avec accessibilité
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="container mx-auto px-4 py-8 max-w-2xl" role="main">
          <div className="text-center" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des données de votre audit...</p>
          </div>
        </main>
      </div>
    );
  }

  // Gestion des erreurs
  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="container mx-auto px-4 py-8 max-w-2xl" role="main">
          <div className="text-center" role="alert">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-red-800 mb-2">Erreur lors du chargement</h2>
              <p className="text-red-600 mb-4">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                aria-label="Recharger la page"
              >
                Réessayer
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Validation du token
  if (!effectiveToken) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="container mx-auto px-4 py-8 max-w-2xl" role="main">
          <div className="text-center" role="alert">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-full">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-yellow-800 mb-2">Token invalide</h2>
              <p className="text-yellow-600">Le lien d'accès à votre audit semble invalide ou expiré.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-indigo-50">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Hero section avec titre et animation */}
        <ScoreHeroSection score={score} siteUrl={siteUrl} />

        {/* Analyse et recommandations */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden mb-10">
          <div className="p-5 bg-gradient-to-r from-indigo-600 to-blue-700">
            <h2 className="text-2xl font-bold text-white">En bonus : Bilan complet de votre site web</h2>
          </div>
          
          <div className="p-6">
            <PerformanceAnalysisSection 
              performanceLoading={loading}
              performanceError={error}
              performanceAnalysis={instantPageAnalysis}
              activeSection={activeSection}
              setActiveSection={handleSectionChange}
              siteUrl={siteUrl}
              performanceMetrics={performanceMetrics}
            />
            <RecommendationsSection score={score} />
          </div>
        </div>

        {/* Section prise de rendez-vous TidyCal */}
        <TidyCalSection />
      </main>
    </div>
  );
};

export default ScorePage;
